import React, { useState, useEffect, useMemo } from 'react';
import {
  formatMeasuredRange,
  formatMeasuredValue,
  resolveMeasure,
} from '@/utils/format';
import type { CountryCode } from '@/utils/location-detection';
import '../common.scss';
import './styles.scss';

export interface RangeSliderProps {
  name: string;
  required?: boolean;
  disabled?: boolean;
  value?: string;
  options: Array<[number, number]>;
  independentMinMax?: boolean; // Default true for independent mode
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  measure?: string | ((userCountryCode: CountryCode) => string); // Generic measure (currency, units, etc.)
  userCountryCode?: CountryCode; // User's detected country code for dynamic measure
  'aria-label'?: string;
  'aria-describedby'?: string;
}

interface BudgetValue {
  min: number;
  max: number;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  name,
  required = false,
  disabled = false,
  value = '',
  options = [],
  independentMinMax = true, // Default to independent mode
  onChange,
  onBlur,
  className = '',
  measure,
  userCountryCode = 'US', // Default to US for USD
  ...ariaProps
}) => {
  // Flatten and sort all unique values from range tuples into discrete steps
  const steps = useMemo(() => {
    const allValues = options.flatMap(([min, max]) => [min, max]);
    return [...new Set(allValues)].sort((a, b) => a - b);
  }, [options]);

  // Helper function to find which original range tuple contains a value
  const findMatchingRange = (stepValue: number): [number, number] | null => {
    return options.find(([min, max]) => stepValue >= min && stepValue <= max) || null;
  };

  // Parse value from JSON object format {min: number, max: number}
  const parseValue = (val: string): [number, number] => {
    if (!val || steps.length === 0) return [0, steps.length - 1];

    try {
      const parsed = JSON.parse(val) as BudgetValue;

      // Find step indices for min and max values
      const minStepIndex = steps.findIndex(step => step === parsed.min);
      const maxStepIndex = steps.findIndex(step => step === parsed.max);

      return [
        minStepIndex >= 0 ? minStepIndex : 0,
        maxStepIndex >= 0 ? maxStepIndex : steps.length - 1,
      ];
    } catch {
      // Default to first range if parsing fails
      return [0, steps.length - 1];
    }
  };

  const [minValue, maxValue] = parseValue(value);
  const [minStepIndex, setMinStepIndex] = useState(minValue);
  const [maxStepIndex, setMaxStepIndex] = useState(maxValue);
  const maxSliderIndex = steps.length - 1;

  // Resolve measure:
  // If measure is a function, call it with userCountryCode
  // Otherwise use measure string directly
  // Resolve currency codes to symbols (e.g., GBP -> £)
  const measureSymbol = useMemo(() => {
    if (!measure) return '';

    const rawMeasure = typeof measure === 'function' ? measure(userCountryCode) : measure;
    return resolveMeasure(rawMeasure);
  }, [measure, userCountryCode]);

  // Update slider values when prop value changes
  useEffect(() => {
    const [newMin, newMax] = parseValue(value);
    if (newMin !== minStepIndex || newMax !== maxStepIndex) {
      setMinStepIndex(newMin);
      setMaxStepIndex(newMax);
    }
  }, [value, steps, minStepIndex, maxStepIndex]);

  const updateValue = (minVal: number, maxVal: number) => {
    if (onChange) {
      // Create object with actual min/max values
      const budgetValue: BudgetValue = {
        min: minVal,
        max: maxVal,
      };

      // Send as JSON string
      const jsonValue = JSON.stringify(budgetValue);

      const syntheticEvent = {
        target: { value: jsonValue, name },
        currentTarget: { value: jsonValue, name },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  // Handle min thumb change with conditional logic based on mode
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMinIndex = parseInt(e.target.value, 10);
    const newMinValue = steps[newMinIndex];

    if (independentMinMax) {
      // INDEPENDENT MODE: Just move min thumb, constrain to not exceed max
      const constrainedMinIndex = Math.min(newMinIndex, maxStepIndex);
      setMinStepIndex(constrainedMinIndex);
      updateValue(steps[constrainedMinIndex], steps[maxStepIndex]);
    } else {
      // OPTION C MODE: Find matching range and auto-adjust max
      const matchingRange = findMatchingRange(newMinValue);

      if (matchingRange) {
        const [rangeMin, rangeMax] = matchingRange;
        const maxStepIdx = steps.findIndex(step => step === rangeMax);

        setMinStepIndex(newMinIndex);
        setMaxStepIndex(maxStepIdx >= 0 ? maxStepIdx : newMinIndex);
        updateValue(rangeMin, rangeMax);
      }
    }
  };

  // Handle max thumb change with conditional logic based on mode
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMaxIndex = parseInt(e.target.value, 10);
    const newMaxValue = steps[newMaxIndex];

    if (independentMinMax) {
      // INDEPENDENT MODE: Just move max thumb, constrain to not go below min
      const constrainedMaxIndex = Math.max(newMaxIndex, minStepIndex);
      setMaxStepIndex(constrainedMaxIndex);
      updateValue(steps[minStepIndex], steps[constrainedMaxIndex]);
    } else {
      // OPTION C MODE: Find matching range and auto-adjust min
      const matchingRange = findMatchingRange(newMaxValue);

      if (matchingRange) {
        const [rangeMin, rangeMax] = matchingRange;
        const minStepIdx = steps.findIndex(step => step === rangeMin);

        setMinStepIndex(minStepIdx >= 0 ? minStepIdx : newMaxIndex);
        setMaxStepIndex(newMaxIndex);
        updateValue(rangeMin, rangeMax);
      }
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      const budgetValue: BudgetValue = {
        min: steps[minStepIndex],
        max: steps[maxStepIndex],
      };

      const jsonValue = JSON.stringify(budgetValue);

      const syntheticEvent = {
        target: { value: jsonValue, name },
        currentTarget: { value: jsonValue, name },
      } as React.FocusEvent<HTMLInputElement>;
      onBlur(syntheticEvent);
    }
  };

  // Handle step label click with conditional logic based on mode
  const handleStepClick = (stepIndex: number) => {
    const clickedValue = steps[stepIndex];

    // Calculate distance from clicked step to current min and max
    const distanceToMin = Math.abs(stepIndex - minStepIndex);
    const distanceToMax = Math.abs(stepIndex - maxStepIndex);

    if (independentMinMax) {
      // INDEPENDENT MODE: Just move nearest thumb
      if (distanceToMin <= distanceToMax) {
        // Move min thumb
        const constrainedMinIndex = Math.min(stepIndex, maxStepIndex);
        setMinStepIndex(constrainedMinIndex);
        updateValue(steps[constrainedMinIndex], steps[maxStepIndex]);
      } else {
        // Move max thumb
        const constrainedMaxIndex = Math.max(stepIndex, minStepIndex);
        setMaxStepIndex(constrainedMaxIndex);
        updateValue(steps[minStepIndex], steps[constrainedMaxIndex]);
      }
    } else {
      // OPTION C MODE: Apply range matching
      if (distanceToMin <= distanceToMax) {
        // Move min thumb - find matching range and auto-set max
        const matchingRange = findMatchingRange(clickedValue);
        if (matchingRange) {
          const [rangeMin, rangeMax] = matchingRange;
          const maxStepIdx = steps.findIndex(step => step === rangeMax);
          setMinStepIndex(stepIndex);
          setMaxStepIndex(maxStepIdx >= 0 ? maxStepIdx : stepIndex);
          updateValue(rangeMin, rangeMax);
        }
      } else {
        // Move max thumb - find matching range and auto-set min
        const matchingRange = findMatchingRange(clickedValue);
        if (matchingRange) {
          const [rangeMin, rangeMax] = matchingRange;
          const minStepIdx = steps.findIndex(step => step === rangeMin);
          setMinStepIndex(minStepIdx >= 0 ? minStepIdx : stepIndex);
          setMaxStepIndex(stepIndex);
          updateValue(rangeMin, rangeMax);
        }
      }
    }
  };

  // Calculate percentages for slider fill
  const minPercent = maxSliderIndex > 0 ? (minStepIndex / maxSliderIndex) * 100 : 0;
  const maxPercent = maxSliderIndex > 0 ? (maxStepIndex / maxSliderIndex) * 100 : 100;

  // Get current budget range values
  const currentMin = steps[minStepIndex] || 0;
  const currentMax = steps[maxStepIndex] || 0;

  return (
    <div
      className={`peptalk-range-slider ${className}`.trim()}
      {...ariaProps}
    >
      {/* Hidden input for form compatibility */}
      <input
        type="hidden"
        name={name}
        value={value}
        required={required}
        disabled={disabled}
      />

      {/* Slider container */}
      <div className="peptalk-slider-container">
        {/* Track background */}
        <div className="peptalk-slider-track">
          <div
            className="peptalk-slider-range"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`,
            }}
          />
        </div>

        {/* Min slider */}
        <input
          type="range"
          className="peptalk-slider-input peptalk-slider-input--min"
          min={0}
          max={maxSliderIndex}
          step={1}
          value={minStepIndex}
          onChange={handleMinChange}
          onBlur={handleBlur}
          disabled={disabled}
          aria-label={`Minimum ${name}`}
        />

        {/* Max slider */}
        <input
          type="range"
          className="peptalk-slider-input peptalk-slider-input--max"
          min={0}
          max={maxSliderIndex}
          step={1}
          value={maxStepIndex}
          onChange={handleMaxChange}
          onBlur={handleBlur}
          disabled={disabled}
          aria-label={`Maximum ${name}`}
        />

        {/* Labels at each step - show single values, clickable */}
        <div className="peptalk-slider-marks">
          {steps.map((stepValue, index) => (
            <div
              key={index}
              className="peptalk-slider-mark peptalk-slider-mark--clickable"
              style={{
                left: `${maxSliderIndex > 0 ? (index / maxSliderIndex) * 100 : 0}%`,
              }}
              onClick={() => !disabled && handleStepClick(index)}
              role="button"
              tabIndex={disabled ? -1 : 0}
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleStepClick(index);
                }
              }}
            >
              {formatMeasuredValue(stepValue, measureSymbol)}
            </div>
          ))}
        </div>
      </div>

      {/* Selected range display below slider */}
      <div className="peptalk-slider-value">
        {formatMeasuredRange(currentMin, currentMax, measureSymbol)}
      </div>
    </div>
  );
};
